import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { AbstractModuleUi } from '../_abstract/ui';
import { ModuleHeaderUi } from '../_components/ui';
import { BootstrapTextArea, BootstrapInput, BootstrapCheck, BootstrapButton } from '../../dsl/bootstrap';
let AnonymousReportModuleUi = class AnonymousReportModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        this.formData = {
            message: '',
            name: '',
            email: '',
            consent: false
        };
        this.message = '';
        this.isSuccess = false;
    }
    get hasPersonalData() {
        var _a, _b;
        return !!(((_a = this.formData.name) === null || _a === void 0 ? void 0 : _a.trim()) || ((_b = this.formData.email) === null || _b === void 0 ? void 0 : _b.trim()));
    }
    get isFormValid() {
        return this.isMessageValid &&
            (!this.hasPersonalData || this.formData.consent) &&
            this.isNameValid &&
            this.isEmailValid;
    }
    get isNameValid() {
        var _a;
        if (!((_a = this.formData.name) === null || _a === void 0 ? void 0 : _a.trim()))
            return true;
        return this.formData.name.trim().length <= 100;
    }
    get isEmailValid() {
        var _a;
        if (!((_a = this.formData.email) === null || _a === void 0 ? void 0 : _a.trim()))
            return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(this.formData.email.trim()) && this.formData.email.length <= 255;
    }
    get isMessageValid() {
        const messageLength = this.formData.message.trim().length;
        return messageLength >= 10 && messageLength <= 5000;
    }
    get messageClass() {
        return this.isSuccess ? 'text-success' : 'text-danger';
    }
    get processedIntroText() {
        if (!this.module.content.introText)
            return '';
        // Regex to match #PGP-KEY-LINK[content]
        const pgpKeyLinkRegex = /#PGP-KEY-LINK\[([^\]]+)\]/g;
        return this.module.content.introText.replace(pgpKeyLinkRegex, (match, linkText) => {
            return `<a href="javascript:void(0)" class="pgp-key-link" data-action="download-pgp-key">${linkText}</a>`;
        });
    }
    submitForm() {
        if (!this.isFormValid) {
            return;
        }
        this.onSubmit(this.formData).then(() => {
            this.message = this.$t('modules.anonymousReport.successMessage').toString();
            this.isSuccess = true;
            this.resetForm();
        }).catch(() => {
            this.message = this.$t('modules.anonymousReport.errorMessage').toString();
            this.isSuccess = false;
        });
    }
    resetForm() {
        this.formData = {
            message: '',
            name: '',
            email: '',
            consent: false
        };
    }
    handleIntroClick(event) {
        const target = event.target;
        if (target.tagName === 'A' && target.getAttribute('data-action') === 'download-pgp-key') {
            event.preventDefault();
            this.downloadPgpKey();
        }
    }
    downloadPgpKey() {
        this.onDownloadPgpKey().catch(() => {
            console.error('Failed to download PGP key');
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSubmit(formData) {
        // NOTE: This function HAS TO be overridden in client
        return Promise.resolve();
    }
    onDownloadPgpKey() {
        // NOTE: This function HAS TO be overridden in client
        return Promise.resolve();
    }
};
AnonymousReportModuleUi = __decorate([
    Component({
        name: 'AnonymousReportModuleUi',
        components: {
            ModuleHeaderUi,
            BootstrapTextArea,
            BootstrapInput,
            BootstrapCheck,
            BootstrapButton
        },
        template: `
    <section class="anonymous-report-module">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <ModuleHeaderUi v-if="module.content.header" :header="module.content.header" />
            <div v-if="module.content.introText" class="intro-text mb-4" v-html="processedIntroText" @click="handleIntroClick"></div>

            <form class="row justify-content-center" @submit.prevent="submitForm">
              <div class="col-12">
                <!-- Message -->
                <BootstrapTextArea
                  name="message"
                  :model.sync="formData.message"
                  :class="{ 'is-invalid': !isMessageValid && formData.message.length > 0 }"
                  maxlength="5000"
                  :rows="10"
                  required
                  :label="$t('modules.anonymousReport.messageLabel')"
                  :aria-describedby="!isMessageValid ? 'message-error' : undefined"
                />
                <div v-if="!isMessageValid && formData.message.length > 0" id="message-error" class="invalid-feedback">
                  {{ $t('modules.anonymousReport.messageError') }}
                </div>
                <small class="form-text text-muted">
                  {{ $t('modules.anonymousReport.charactersRemaining', { count: formData.message.length, max: 5000 }) }}
                </small>

                <!-- Name -->
                <BootstrapInput
                  name="name"
                  :model.sync="formData.name"
                  :class="{ 'is-invalid': !isNameValid && formData.name && formData.name.length > 0 }"
                  type="text"
                  maxlength="100"
                  :label="$t('modules.anonymousReport.nameLabel')"
                  :aria-describedby="!isNameValid ? 'name-error' : undefined"
                  class="mt-3"
                />
                <div v-if="!isNameValid && formData.name && formData.name.length > 0" id="name-error" class="invalid-feedback">
                  {{ $t('modules.anonymousReport.nameError') }}
                </div>

                <!-- Email -->
                <BootstrapInput
                  name="email"
                  :model.sync="formData.email"
                  :class="{ 'is-invalid': !isEmailValid && formData.email && formData.email.length > 0 }"
                  type="email"
                  maxlength="255"
                  :label="$t('modules.anonymousReport.emailLabel')"
                  :aria-describedby="!isEmailValid ? 'email-error' : undefined"
                  class="mt-3"
                />
                <div v-if="!isEmailValid && formData.email && formData.email.length > 0" id="email-error" class="invalid-feedback">
                  {{ $t('modules.anonymousReport.emailError') }}
                </div>

                <!-- Consent -->
                <div v-if="hasPersonalData" class="text-start">
                  <BootstrapCheck
                    name="consent"
                    :model.sync="formData.consent"
                    :options="[{
                      label: $t('modules.anonymousReport.consentText'),
                      value: true
                    }]"
                    :multiple="true"
                    required
                    :class="{ 'is-invalid': hasPersonalData && !formData.consent }"
                    class="mt-3"
                  />
                </div>

                <p v-if="message !== ''" class="text-center" :class="messageClass">{{ message }}</p>

                <!-- Submit button -->
                <div class="d-flex justify-content-center mt-5">
                  <BootstrapButton
                    type="submit"
                    :disabled="!isFormValid"
                    class="btn-cta"
                  >
                    {{ $t('modules.anonymousReport.submitButton') }}
                  </BootstrapButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `
    })
], AnonymousReportModuleUi);
export { AnonymousReportModuleUi };
